// Generated by Claude Code
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '#i18n.ts'
import { cn } from '#core/utils/cn.ts'

export interface LanguageSelectProps {
  /** Extra classes appended after the base `form-select form-select-sm`. */
  className?: string
  /** Inline styles forwarded to the underlying `<select>` (e.g. max-width). */
  style?: React.CSSProperties
  /** Accessible label; falls back to the shared "language" string when omitted. */
  'aria-label'?: string
  /** Optional id for pairing with an external `<label htmlFor>`. */
  id?: string
  /**
   * Extra side-effect invoked with the newly selected code, in addition to
   * switching i18next's active language. Use it to persist the choice (e.g. to
   * the user's general settings). The language change itself is handled here.
   */
  onLanguageChange?: (language: string) => void
}

/**
 * The single, shared language picker. Reads the active UI language from i18next
 * and switches it via `i18n.changeLanguage` on change, rendering one option per
 * {@link SUPPORTED_LANGUAGES} (labelled by uppercase code). Call sites pass a
 * `className`/`style` to keep their existing look and an optional
 * `onLanguageChange` to also persist the choice.
 */
export function LanguageSelect({
  className,
  style,
  id,
  onLanguageChange,
  'aria-label': ariaLabel,
}: LanguageSelectProps) {
  const { t, i18n } = useTranslation()

  const handleChange = (code: string) => {
    void i18n.changeLanguage(code)
    onLanguageChange?.(code)
  }

  return (
    <select
      id={id}
      className={cn('form-select form-select-sm', className)}
      style={style}
      value={i18n.language}
      onChange={(e) => handleChange(e.target.value)}
      aria-label={ariaLabel ?? t('shared.fields.language')}
    >
      {SUPPORTED_LANGUAGES.map((code) => (
        <option key={code} value={code}>
          {code.toUpperCase()}
        </option>
      ))}
    </select>
  )
}

export default LanguageSelect
